import React, { useState, useEffect, Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { motion } from "framer-motion";
import { BRAND, LogoMark } from "./shared";
import { portfolioVideos } from "./content/videos";
import { siteConfig } from "./content/siteConfig";
import StructuredData from "./components/StructuredData";
import ScrollToTop from "./components/ScrollToTop";
import BackToTopButton from "./components/BackToTopButton";
import ProtectedRoute from "./components/ProtectedRoute";
import { CartProvider } from "./context/CartContext";
import { AuthProvider } from "./context/AuthContext";

// Lazy load pages for better performance
const Home = lazy(() => import("./pages/Home"));
const Services = lazy(() => import("./pages/Services"));
const Shop = lazy(() => import("./pages/Shop"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Cart = lazy(() => import("./pages/Cart"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const Photography = lazy(() => import("./pages/Photography"));
const VideoProduction = lazy(() => import("./pages/VideoProduction"));
const Design = lazy(() => import("./pages/Design"));
const Signage = lazy(() => import("./pages/Signage"));
const SocialMedia = lazy(() => import("./pages/SocialMedia"));
const MarketingStrategy = lazy(() => import("./pages/MarketingStrategy"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Contact = lazy(() => import("./pages/Contact"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const Activities = lazy(() => import("./pages/Activities"));
const Booking = lazy(() => import("./pages/Booking"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const Account = lazy(() => import("./pages/Account"));
const OrderHistory = lazy(() => import("./pages/OrderHistory"));
const Privacy = lazy(() => import("./pages/Privacy"));

// No redirect handler needed - Cloudflare Pages Function handles SPA routing

export default function MarketingForSchoolsApp() {
  const [modal, setModal] = useState<string | null>(null);

  // Inject favicon (Club Wild logo)
  useEffect(() => {
    const href = '/favicon.png';
    const rels = ['icon', 'shortcut icon'];
    rels.forEach(rel => {
      let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
      link.setAttribute('type', 'image/png');
    });
  }, []);

  return (
    <HelmetProvider>
      <AuthProvider>
        <CartProvider>
          <Router>
            <ScrollToTop />
            <StructuredData />
            <div className="min-h-screen bg-white text-[#111] antialiased pt-20">
              <Header />

        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center">
            <div className="text-center">
              <LogoMark size={64} className="animate-pulse mx-auto mb-4" />
              <p className="text-neutral-600">Loading...</p>
            </div>
          </div>
        }>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/activities" element={<Activities />} />
            <Route path="/booking" element={<Booking />} />
            <Route path="/services" element={<Services />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/shop/:slug" element={<ProductDetail />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/checkout/success" element={<CheckoutSuccess />} />

            {/* Service pages - with and without /services/ prefix */}
            <Route path="/services/photography" element={<Photography />} />
            <Route path="/photography" element={<Photography />} />

            <Route path="/services/video-production" element={<VideoProduction />} />
            <Route path="/video-production" element={<VideoProduction />} />

            <Route path="/services/design" element={<Design />} />
            <Route path="/design" element={<Design />} />

            <Route path="/services/signage" element={<Signage />} />
            <Route path="/signage" element={<Signage />} />

            <Route path="/services/social-media" element={<SocialMedia />} />
            <Route path="/social-media" element={<SocialMedia />} />

            <Route path="/services/marketing-strategy" element={<MarketingStrategy />} />
            <Route path="/marketing-strategy" element={<MarketingStrategy />} />

            <Route path="/our-work" element={<Gallery />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/contact/thank-you" element={<ThankYou />} />
            <Route path="/privacy" element={<Privacy />} />

            {/* Account & Authentication */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
            <Route path="/account/orders" element={<ProtectedRoute><OrderHistory /></ProtectedRoute>} />
          </Routes>
        </Suspense>

        <Footer />

        {/* Floating Back to Top Button */}
        <BackToTopButton />

        {modal && (
          <VideoModal url={portfolioVideos.find((v) => v.id === modal)?.youtube ?? ""} onClose={() => setModal(null)} />
        )}
          </div>
          </Router>
        </CartProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

function Header() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Cart and auth removed - shop is hidden
  // const { getItemCount } = useCart();
  // const { isAuthenticated } = useAuth();
  // const cartItemCount = getItemCount();

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileMenuOpen) setMobileMenuOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [mobileMenuOpen]);

  return (
    <header className="fixed top-0 left-0 right-0 z-40 bg-white/70 backdrop-blur-sm shadow-lg" style={{ borderBottom: `2px solid ${BRAND.green}` }}>
      {/* Skip to main content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 bg-neutral-900 text-white px-4 py-2 rounded-md font-semibold"
      >
        Skip to main content
      </a>

      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo + Company Name - Left */}
          <Link to="/" className="flex items-center gap-3 hover:scale-105 transition-transform">
            <LogoMark size={48} />
            <span
              className="font-bold text-2xl text-[#333144] transition-colors"
              style={{ color: '#333144' }}
              onMouseEnter={(e) => e.currentTarget.style.color = BRAND.green}
              onMouseLeave={(e) => e.currentTarget.style.color = '#333144'}
            >
              {siteConfig.businessName}
            </span>
          </Link>

          {/* Navigation Links - Desktop */}
          <nav className="hidden md:flex items-center gap-8" aria-label="Main navigation">
            {siteConfig.navigation.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-base transition-all ${
                  location.pathname === item.path
                    ? 'font-bold pb-1'
                    : 'font-normal hover:font-bold hover:scale-110'
                }`}
                style={{
                  color: location.pathname === item.path ? BRAND.green : '#5d5d5d',
                  borderBottom: location.pathname === item.path ? `3px solid ${BRAND.green}` : 'none',
                }}
                onMouseEnter={(e) => {
                  if (location.pathname !== item.path) {
                    e.currentTarget.style.color = BRAND.green;
                  }
                }}
                onMouseLeave={(e) => {
                  if (location.pathname !== item.path) {
                    e.currentTarget.style.color = '#5d5d5d';
                  }
                }}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Account & Shopping Cart Icons - HIDDEN (shop disabled) */}

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-12 h-12 flex items-center justify-center rounded-lg border-2 bg-white transition-colors"
            style={{ borderColor: BRAND.green }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${BRAND.green}10`}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: BRAND.green }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: BRAND.green }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <motion.nav
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden mt-4 py-4"
            style={{ borderTop: `2px solid ${BRAND.green}20` }}
            aria-label="Mobile navigation"
          >
            <div className="flex flex-col gap-2">
              {siteConfig.navigation.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-3 rounded-lg font-bold text-lg transition-all ${
                    location.pathname === item.path
                      ? 'text-white'
                      : 'bg-white text-[#333144]'
                  }`}
                  style={{
                    backgroundColor: location.pathname === item.path ? BRAND.green : 'white',
                    border: location.pathname === item.path ? 'none' : `2px solid ${BRAND.green}20`,
                  }}
                  onMouseEnter={(e) => {
                    if (location.pathname !== item.path) {
                      e.currentTarget.style.backgroundColor = `${BRAND.green}10`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (location.pathname !== item.path) {
                      e.currentTarget.style.backgroundColor = 'white';
                    }
                  }}
                  aria-current={location.pathname === item.path ? 'page' : undefined}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </motion.nav>
        )}
      </div>
    </header>
  );
}


function VideoModal({ url, onClose }: { url: string; onClose: () => void }) {
  // Handle escape key to close modal
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
      role="dialog"
      aria-modal="true"
      aria-label="Video player"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-5xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10">
          <iframe
            className="h-full w-full"
            src={`${url}&autoplay=1&mute=1`}
            title="Marketing For Schools Video Player"
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.4 }}
          onClick={onClose}
          aria-label="Close video player"
          className="absolute -top-10 right-0 text-white/90 hover:text-white text-sm"
        >
          Close ✕
        </motion.button>
      </motion.div>
    </div>
  );
}

function Footer() {
  return (
    <footer
      style={{
        background: `linear-gradient(to right, ${BRAND.green}, ${BRAND.greenDark})`,
        borderTop: `4px solid ${BRAND.orange}`,
      }}
    >
      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Partner Companies - Logo Strip */}
        <div className="mb-12 text-center">
          <div className="text-sm text-white/70 mb-4 font-semibold uppercase tracking-wide">Our Partners</div>
          <div className="flex flex-wrap items-center justify-center gap-8">
            {/* 123 Logo */}
            <div className="bg-white px-4 py-3 rounded-lg shadow-lg" style={{ height: '80px', display: 'flex', alignItems: 'center' }}>
              <img src="/partner-123.png" alt="123 Partner" style={{ height: '64px', width: 'auto' }} />
            </div>
            {/* T&E Logo */}
            <div className="bg-white px-4 py-3 rounded-lg shadow-lg" style={{ height: '80px', display: 'flex', alignItems: 'center' }}>
              <img src="/partner-te.jpg" alt="T&E Partner" style={{ height: '64px', width: 'auto' }} />
            </div>
            {/* Club Wild Logo */}
            <div className="bg-white px-4 py-3 rounded-lg shadow-lg" style={{ height: '80px', display: 'flex', alignItems: 'center' }}>
              <img src="/club-wild-logo.png" alt="Club Wild" style={{ height: '64px', width: 'auto' }} />
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white rounded-2xl shadow-lg">
              <LogoMark size={48} />
            </div>
            <div>
              <div className="font-bold text-xl text-white">{siteConfig.businessName}</div>
              <div className="text-sm text-white/80">© 2025 by Club Wild LTD</div>
            </div>
          </div>

          <div className="flex items-center gap-6 text-base">
            {/* Facebook */}
            <a
              href="https://www.facebook.com/clubwildadventures"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/90 hover:text-orange transition-all font-bold hover:scale-110 flex items-center gap-2"
              style={{ color: 'rgba(255,255,255,0.9)' }}
              onMouseEnter={(e) => e.currentTarget.style.color = BRAND.orange}
              onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255,255,255,0.9)'}
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              Facebook
            </a>
            <span className="text-white/30">•</span>
            {/* Instagram */}
            <a
              href="https://www.instagram.com/clubwildadventures"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/90 hover:text-orange transition-all font-bold hover:scale-110 flex items-center gap-2"
              style={{ color: 'rgba(255,255,255,0.9)' }}
              onMouseEnter={(e) => e.currentTarget.style.color = BRAND.orange}
              onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255,255,255,0.9)'}
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/>
              </svg>
              Instagram
            </a>
            <span className="text-white/30">•</span>
            <a
              href={`mailto:${siteConfig.contact.email}`}
              className="text-white/90 hover:text-orange transition-all font-bold hover:scale-110"
              style={{ color: 'rgba(255,255,255,0.9)' }}
              onMouseEnter={(e) => e.currentTarget.style.color = BRAND.orange}
              onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255,255,255,0.9)'}
            >
              Email
            </a>
            <span className="text-white/30">•</span>
            <Link
              to="/privacy"
              className="text-white/90 hover:text-orange transition-all font-bold hover:scale-110"
              style={{ color: 'rgba(255,255,255,0.9)' }}
              onMouseEnter={(e) => (e.currentTarget as HTMLElement).style.color = BRAND.orange}
              onMouseLeave={(e) => (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.9)'}
            >
              Privacy Policy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
